import simpleStageBackground2x from '@/assets/images/background_lg_xl-min-2x.jpg'
import simpleStageBackground from '@/assets/images/background_lg_xl-min.jpg'
import bundleStageBackground2x from '@/assets/images/pdp_bundle_stage_bg-min-2x.jpg'
import bundleStageBackground from '@/assets/images/pdp_bundle_stage_bg-min.jpg'
import router, { FALLBACK_ROUTE_NAME } from '@/router'
import { nextTick } from 'vue'

const IMAGE_PREFIXES = {
  IMAGE_RETINA:
    'https://www.vicampo.de/media/cache/10000/image/x1400/proportional/x/4614b7f85b/',
  IMAGE:
    'https://www.vicampo.de/media/cache/10000/image/x700/proportional/x/1f469530b3/',
  SMALL_IMAGE_RETINA:
    'https://www.vicampo.de/media/cache/10000/small_image/400x/crop_bottom/400x520/353c25da18/',
  SMALL_IMAGE:
    'https://www.vicampo.de/media/cache/10000/small_image/200x/crop_bottom/200x260/a9b50a3e3a/'
}

// Static asset imports resolve to public URLs in both the client and the SSR
// bundle — `new URL(..., import.meta.url)` produced file:// paths on the
// server (VIP-12037)
export const BACKGROUNDS = {
  BUNDLE: {
    X: bundleStageBackground,
    XX: bundleStageBackground2x
  },
  SIMPLE: {
    X: simpleStageBackground,
    XX: simpleStageBackground2x
  }
}

const BASE_REGEXP = /%s/
const STATUS_FORBIDDEN = 403

class UtilityHelper {
  static formatString(subject, ...data) {
    if (!subject) return ''

    if (BASE_REGEXP.test(subject))
      return data.reduce(
        (result, replace) => result.replace(BASE_REGEXP, replace),
        subject
      )

    return data.reduce(
      (result, replace, i) =>
        result.replace(new RegExp(String.raw`\{${i}\}`, 'gm'), replace),
      subject
    )
  }

  static getRandomHash() {
    return Math.random().toString(36).substring(2, 7)
  }

  /** Srcset attribute for picture tags */
  static generateSrcSet(imageSrc, retinaSrc) {
    imageSrc &&= `${imageSrc} 1x`
    retinaSrc &&= `${retinaSrc} 2x`

    // default to empty string if no value is set
    imageSrc ||= ''
    retinaSrc ||= ''

    return imageSrc && retinaSrc
      ? `${imageSrc}, ${retinaSrc}`
      : imageSrc + retinaSrc
  }

  /**
   * Prefixes the product images
   * Can be used when the picture url is relative
   *
   * @typedef {Object} ImageData
   * @property {string} src
   * @property {string} retina_src
   *
   * @returns {ImageData}
   */
  static prefixImage(imageSrc, useFullBottleImage = false) {
    // prevent console error because of invalid url
    if (!imageSrc) {
      return { src: '', retina_src: '' }
    }
    return useFullBottleImage
      ? {
          src: IMAGE_PREFIXES.IMAGE + imageSrc,
          retina_src: IMAGE_PREFIXES.IMAGE_RETINA + imageSrc
        }
      : {
          src: IMAGE_PREFIXES.SMALL_IMAGE + imageSrc,
          retina_src: IMAGE_PREFIXES.SMALL_IMAGE_RETINA + imageSrc
        }
  }

  static isSnakeCase(string) {
    if (!string) {
      return false
    }
    const regex = string.match(/[a-z|0-9]+(_[a-z|0-9]+)*/g)
    return regex && regex.join('').length === string.length
  }

  static stringElementsStartWithText(string, needle) {
    const lowerCaseString = string.toLowerCase()
    const lowerCaseNeedle = needle.toLowerCase()

    const parts = lowerCaseString
      .split(' ')
      .filter((part) => part.startsWith(lowerCaseNeedle))
    return parts.length > 0
  }

  /**
   * Report an error to New Relic via the backend (single channel: ApiController.logFrontendError).
   * Use for cart, wishlist, tracking, etc. when there is no user-facing message.
   */
  static async noticeNRError(error, messagePrefix = '', source = 'manual_log') {
    await nextTick()
    if (globalThis.window == null) return
    try {
      let errorObj
      if (typeof error === 'string') {
        errorObj = new Error(error)
      } else if (error instanceof Error) {
        errorObj = error
      } else {
        errorObj = new Error(String(error?.message ?? error))
      }
      if (messagePrefix) {
        errorObj.message = `${messagePrefix} ${errorObj.message}`
      }
      const ApiController = (await import('@/services/ApiController')).default
      ApiController.logFrontendError(errorObj, source, 'notice')
    } catch (e) {
      console.error('Failed to log frontend error:', e)
    }
  }

  static uuid() {
    if (typeof window === 'undefined' || !window.crypto?.randomUUID) {
      return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (c ^ ((Math.random() * 16) & (15 >> (c / 4)))).toString(16)
      )
    }
    return window.crypto.randomUUID()
  }

  static extractStreetNumber(addressLineOne, addressLineTwo) {
    const number = _extractStreetNumberFromLine(addressLineOne)
    if (number) return number

    if (addressLineTwo && addressLineTwo.length < 7) {
      return _extractStreetNumberFromLine(addressLineTwo)
    }

    return ''
  }

  /**
   * Set or clear the original-customer-id cookie for admin impersonation
   * @param {string|null} customerId - The customer ID to set, or null/empty to clear
   * @param {Object} options - Additional options
   * @param {boolean} options.refreshCart - Whether to refresh the cart after setting (default: true)
   * @param {boolean} options.reloadPage - Whether to reload the page after setting (default: false)
   * @returns {Promise<void>}
   */
  static async setOriginalCustomerId(customerId, options = {}) {
    const { refreshCart = true, reloadPage = false } = options

    try {
      const { default: CookieHelper } =
        await import('@/services/helpers/CookieHelper')

      // Always invalidate cartUuid when changing customer ID
      CookieHelper.invalidate('cartUuid')

      if (customerId?.trim()) {
        if (!Number.isInteger(Number(customerId.trim()))) {
          throw new TypeError(
            'Customer ID must be an integer ' + customerId.trim()
          )
        }
        // Set the original-customer-id cookie
        CookieHelper.setCookie('original-customer-id', customerId.trim(), 1)
      } else {
        // Clear the original-customer-id cookie
        CookieHelper.invalidate('original-customer-id')
      }

      // Refresh cart if requested
      if (refreshCart) {
        const { useCartStore } = await import('@/stores/cart')
        const cartStore = useCartStore()
        await cartStore.refreshCart()
      }

      // Reload page if requested
      if (reloadPage && typeof window !== 'undefined') {
        setTimeout(() => {
          window.location.reload()
        }, 100)
      }
    } catch (error) {
      console.error('Error setting original customer ID:', error)
    }
  }
}

export const redirectToSuccessPage = async (redirectUrl) => {
  const url = new URL(redirectUrl)
  const name = router.resolve(url.pathname).name
  if (name !== FALLBACK_ROUTE_NAME) {
    await router.push(url.pathname)
  } else {
    window.location.href = redirectUrl
  }
}

export const debounce = (func, delay) => {
  let timer
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func(...args)
    }, delay)
  }
}

export const throttle = (func, delay) => {
  let wait = false
  let waitingArgs
  const handler = () => {
    if (!waitingArgs) {
      wait = false
      return
    }
    func(...waitingArgs)
    waitingArgs = null
    setTimeout(handler, delay)
  }

  return (...args) => {
    if (wait) {
      waitingArgs = args
      return
    }

    func(...args)
    wait = true

    setTimeout(handler, delay)
  }
}

const _extractStreetNumberFromLine = (line = '') => {
  const match = new RegExp(/(\d+).{0,2}/).exec(line)
  return match ? match[0] : ''
}

const _mediaQuery = (query) => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false
  }
  return matchMedia(query).matches
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const { useCustomerStore } = await import('@/stores/customer')
    // Getter, not a resolved instance — see VIP-12037 (per-request pinia).
    cache = {
      get customerStore() {
        return useCustomerStore()
      }
    }
    return cache
  }
})()

export default UtilityHelper
